/* eslint-disable */
import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSeoFetch } from '@/hooks/useSeoFetch';
import useSeoProject from '@/hooks/useSeoProject';
import { FetchApiIds } from '@/types';
import seoPagesService from '@/services/seoPages';
import seoKeywordsService from '@/services/seoKeywords';
import seoImpactService from '@/services/seoImpact';
import seoCopilotService from '@/services/seoCopilot';
import seoTasksService from '@/services/seoTasks';
import wordpressService from '@/services/wordpress';
import seoAiService, { ServicePageHealthResult } from '@/services/seoAi';
import {
  SeoPage,
  SeoTrackedKeyword,
  SeoTask,
  WpAppliedChange,
  SeoCopilotSession,
} from '@/types/api/seo';
import { useBacklinkProfile } from '@/hooks/seo/useBacklinkProfile';
import { useKeywordDifficulty } from '@/hooks/seo/useKeywordIntelligence';
import SeoPageDetailComponent from './SeoPageDetail.component';

type KeywordsPaginated = {
  data: SeoTrackedKeyword[];
  count: number;
  currentPage: number;
  lastPage: number;
  nextPage: number | null;
  prevPage: number | null;
};

const SeoPageDetailPage = function (): JSX.Element {
  const { pageId, projectId = '' } = useParams();
  const { project, setBreadcrumbDetail } = useSeoProject();

  // Page data
  const { data: page, isLoading: isLoadingPage } = useSeoFetch<SeoPage>(
    ['seo-page-by-id', pageId],
    () => seoPagesService.getById(pageId as string),
    { enabled: !!pageId }
  );

  // All keywords for this project
  const { data: keywordsData } = useSeoFetch<KeywordsPaginated>(
    [FetchApiIds.SEO_KEYWORDS, projectId],
    () => seoKeywordsService.getAll({ projectId, page: 1, take: 500 }),
    { enabled: !!projectId }
  );

  // Filter keywords targeting this page
  const pageKeywords = useMemo(() => {
    const allKws = keywordsData?.data ?? [];
    if (!page) return [];
    return allKws.filter((kw) => {
      if (kw.targetPageId === pageId) return true;
      if (kw.latestSnapshot?.url === page.url) return true;
      return false;
    });
  }, [keywordsData, page, pageId]);

  // KD data for page keywords
  const kwTexts = useMemo(() => pageKeywords.map((k) => k.keyword), [pageKeywords]);
  const { difficultyMap } = useKeywordDifficulty(kwTexts);

  // Ahrefs domain data
  const {
    latestSnapshot: ahrefsDomainSnapshot,
    newBacklinks,
    lostBacklinks,
  } = useBacklinkProfile(projectId || undefined);

  // Applied changes for page keywords
  const keywordIds = useMemo(() => pageKeywords.map((k) => k.id), [pageKeywords]);
  const { data: allChanges = [], refetch: refetchChanges } = useSeoFetch<WpAppliedChange[]>(
    ['seo-wp-changes', projectId],
    () => seoImpactService.getByProject(projectId),
    { enabled: !!projectId }
  );
  const pageChanges = useMemo(() => {
    if (!page) return [];
    return allChanges.filter(
      (c) => keywordIds.includes(c.keywordId ?? '') || c.pageUrl === page.url
    );
  }, [allChanges, keywordIds, page]);

  // Copilot sessions for keywords
  const { data: copilotSessions = [] } = useSeoFetch<(SeoCopilotSession | null)[]>(
    ['seo-copilot-sessions-page', pageId, keywordIds.join(',')],
    async () => {
      const results = await Promise.all(
        pageKeywords.map((kw) => seoCopilotService.getSessionForKeyword(kw.id).catch(() => null))
      );
      return results;
    },
    { enabled: pageKeywords.length > 0 }
  );

  const copilotByKeyword = useMemo(() => {
    const map = new Map<string, SeoCopilotSession>();
    copilotSessions.forEach((session, i) => {
      if (session && pageKeywords[i]) map.set(pageKeywords[i].id, session);
    });
    return map;
  }, [copilotSessions, pageKeywords]);

  // Service page health check
  const [healthResult, setHealthResult] = useState<ServicePageHealthResult | null>(null);
  const [isCheckingHealth, setIsCheckingHealth] = useState(false);

  // All site pages for sibling detection
  const { data: allPages = [] } = useSeoFetch<SeoPage[]>(
    ['seo-project-pages', projectId],
    () => seoPagesService.getByProject(projectId),
    { enabled: !!projectId }
  );

  const isServicePage = useMemo(() => {
    if (!page) return false;
    const lower = page.url.toLowerCase();
    return (
      /\/(hire|find|outsource|engage|staff|book)[-/]/.test(lower) ||
      /developers?\/?$|engineers?\/?$/i.test(lower)
    );
  }, [page]);

  const handleCheckHealth = useCallback(async () => {
    if (!page) return;
    setIsCheckingHealth(true);
    try {
      const sitePages = allPages.map((p) => ({ page: p.url }));
      const result = await seoAiService.analyzeServicePageHealth({ pageUrl: page.url, sitePages });
      setHealthResult(result);
    } catch {
      // silently fail
    } finally {
      setIsCheckingHealth(false);
    }
  }, [page, allPages]);

  // Auto-run health check for service pages
  useEffect(() => {
    if (isServicePage && page && !healthResult && !isCheckingHealth) {
      handleCheckHealth();
    }
  }, [isServicePage, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // FAQ preview workflow — generate → preview → publish
  const [generatedFaqs, setGeneratedFaqs] = useState<Array<{
    question: string;
    answer: string;
  }> | null>(null);
  const [generatedFaqSchema, setGeneratedFaqSchema] = useState<string | null>(null);
  const [isGeneratingFaqs, setIsGeneratingFaqs] = useState(false);
  const [isPublishingFaqs, setIsPublishingFaqs] = useState(false);

  const handleGenerateFaqs = useCallback(async () => {
    if (!page) return;
    setIsGeneratingFaqs(true);
    setGeneratedFaqs(null);
    setGeneratedFaqSchema(null);

    // For page-level actions, always derive keyword from the page URL/title — not from associated keywords
    // because a page might have unrelated keywords assigned to it
    const primaryKw = pageKeywords[0] ?? null;
    const pathKeyword = page.path.replace(/^\//, '').replace(/\/$/, '').replace(/-/g, ' ').trim();
    const keyword = pathKeyword || page.title || primaryKw?.keyword || '';
    const snap = primaryKw?.latestSnapshot;

    console.log('[FAQ Generate] Page path:', page.path, '→ keyword:', keyword);

    try {
      console.log('[FAQ Generate] Calling AI with keyword:', keyword, 'pageUrl:', page.url);
      const result = await seoAiService.getOpportunitySuggestions({
        keyword,
        opportunityType: 'service_page_faq_schema',
        opportunityTitle: 'Generate FAQ Schema',
        opportunityDescription: 'Generate technology-specific FAQs with FAQPage JSON-LD schema',
        position: snap?.position ?? null,
        ctr: snap?.ctr ?? null,
        clicks: snap?.clicks ?? null,
        impressions: snap?.impressions ?? null,
        serpSummary: null,
        pageUrl: page.url,
      });

      console.log('[FAQ Generate] Raw result:', result);
      const suggestions = result?.suggestions ?? [];
      console.log(
        '[FAQ Generate] Suggestions count:',
        suggestions.length,
        'types:',
        suggestions.map((s: any) => s.type)
      );

      // Parse FAQ entries from suggestions
      const faqs: Array<{ question: string; answer: string }> = [];
      let schemaJson = '';

      for (const sug of suggestions) {
        // Normalize value to string
        const val =
          typeof sug.value === 'string'
            ? sug.value
            : sug.value != null
            ? JSON.stringify(sug.value)
            : '';
        if (!val) continue;

        if (sug.type === 'faq_entry') {
          const qMatch = sug.label?.match(/^(?:FAQ:\s*)?(.+\?)/i);
          const question = qMatch?.[1] ?? sug.label ?? '';
          faqs.push({ question, answer: val });
        } else if (
          sug.type === 'schema_markup' ||
          sug.type === 'schema' ||
          sug.type === 'json_ld'
        ) {
          schemaJson = val
            .replace(/<script[^>]*>/gi, '')
            .replace(/<\/script>/gi, '')
            .trim();
        }
      }

      // If we got FAQ entries but no schema block, build the schema from the FAQs
      if (faqs.length > 0 && !schemaJson) {
        const faqSchema = {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        };
        schemaJson = JSON.stringify(faqSchema, null, 2);
      }

      // If we got a schema block but no parsed FAQs, parse them from the schema
      if (faqs.length === 0 && schemaJson) {
        try {
          const parsed = JSON.parse(schemaJson);
          const entities = parsed.mainEntity ?? [];
          for (const e of entities) {
            faqs.push({ question: e.name ?? '', answer: e.acceptedAnswer?.text ?? '' });
          }
        } catch {
          /* invalid JSON */
        }
      }

      console.log('[FAQ Generate] Parsed FAQs:', faqs.length, 'Schema length:', schemaJson.length);
      if (faqs.length > 0) {
        setGeneratedFaqs(faqs);
        setGeneratedFaqSchema(schemaJson);
      } else {
        setCopilotResult('No FAQ suggestions generated. Try running the full copilot.');
      }
    } catch (err) {
      console.error('[FAQ Generate] Error:', err);
      setCopilotResult(`Failed to generate FAQs: ${(err as Error).message ?? 'unknown error'}`);
    } finally {
      setIsGeneratingFaqs(false);
    }
  }, [page, pageKeywords]);

  const handlePublishFaqs = useCallback(async () => {
    if (!page || !generatedFaqSchema) return;
    setIsPublishingFaqs(true);

    try {
      const wpPost = await wordpressService.findPostByUrl(projectId, page.url);
      if (!wpPost) {
        setCopilotResult('WordPress post not found for this URL.');
        setIsPublishingFaqs(false);
        return;
      }

      // Clean the schema JSON (strip script tags if present)
      const cleanSchema = generatedFaqSchema
        .replace(/<script[^>]*>/gi, '')
        .replace(/<\/script>/gi, '')
        .trim();

      await wordpressService.applyChanges({
        projectId,
        postId: wpPost.postId,
        postType: wpPost.postType,
        keywordId: pageKeywords[0]?.id,
        pageUrl: page.url,
        changes: { schema: cleanSchema },
      });

      // Also create a task record
      const kwId = pageKeywords[0]?.id;
      const kwText = pageKeywords[0]?.keyword ?? page.path ?? '';
      await seoTasksService.create({
        projectId,
        keywordId: kwId ?? null,
        title: `[Copilot] FAQPage Schema — ${generatedFaqs?.length ?? 0} FAQs`,
        description: `Generated and published FAQPage JSON-LD schema with ${
          generatedFaqs?.length ?? 0
        } FAQs for "${kwText}".`,
        taskType: 'schema',
        suggestedValue: cleanSchema,
        priority: 'high',
      });

      setCopilotResult(
        `FAQPage schema with ${generatedFaqs?.length ?? 0} FAQs published to WordPress.`
      );
      setGeneratedFaqs(null);
      setGeneratedFaqSchema(null);
      refetchChanges();
      setTimeout(() => handleCheckHealth(), 3000);
    } catch (err) {
      setCopilotResult(`Failed to publish: ${(err as Error).message}`);
    } finally {
      setIsPublishingFaqs(false);
    }
  }, [
    page,
    generatedFaqSchema,
    generatedFaqs,
    projectId,
    pageKeywords,
    refetchChanges,
    handleCheckHealth,
  ]);

  const handleClearFaqPreview = useCallback(() => {
    setGeneratedFaqs(null);
    setGeneratedFaqSchema(null);
  }, []);

  // Run copilot for all keywords on this page
  const [isRunningCopilot, setIsRunningCopilot] = useState(false);
  const [copilotResult, setCopilotResult] = useState<string | null>(null);

  const handleRunCopilot = useCallback(async () => {
    if (!page || pageKeywords.length === 0) return;
    setIsRunningCopilot(true);
    setCopilotResult(null);

    let started = 0;
    let cycled = 0;
    let errors = 0;

    for (const kw of pageKeywords) {
      try {
        const session = copilotByKeyword.get(kw.id);
        if (session && (session.status === 'active' || session.status === 'paused')) {
          // Trigger a new cycle on existing session
          await seoCopilotService.triggerCycle(session.id);
          cycled++;
        } else if (!session) {
          // Start a new session targeting this page
          await seoCopilotService.startSession(projectId, kw.id, 3, 12, page.url);
          started++;
        }
      } catch {
        errors++;
      }
    }

    setCopilotResult(
      [
        started > 0 ? `${started} copilot session${started > 1 ? 's' : ''} started` : '',
        cycled > 0 ? `${cycled} cycle${cycled > 1 ? 's' : ''} triggered` : '',
        errors > 0 ? `${errors} failed` : '',
      ]
        .filter(Boolean)
        .join(', ') || 'No keywords to optimize'
    );
    setIsRunningCopilot(false);
  }, [page, pageKeywords, copilotByKeyword, projectId]);

  // Fix a specific health check — directly generate suggestions → create task → apply to WordPress
  const [fixingCheck, setFixingCheck] = useState<string | null>(null);

  const wpConnected = !!(project?.wpConnectedAt && project?.wpSiteUrl);

  // Map health check keys to opportunity types and task types
  const CHECK_CONFIG: Record<
    string,
    { opportunityType: string; title: string; taskType: string; wpField: string }
  > = {
    faqSchema: {
      opportunityType: 'service_page_faq_schema',
      title: 'Add FAQ Schema',
      taskType: 'schema',
      wpField: 'schema',
    },
    serviceSchema: {
      opportunityType: 'service_page_schema',
      title: 'Add Service Schema',
      taskType: 'schema',
      wpField: 'schema',
    },
    pricing: {
      opportunityType: 'service_page_pricing',
      title: 'Add Pricing Section',
      taskType: 'content',
      wpField: 'content',
    },
    titleFreshness: {
      opportunityType: 'service_page_freshness',
      title: 'Update Title with Current Month',
      taskType: 'title_tag',
      wpField: 'seoTitle',
    },
    contentDepth: {
      opportunityType: 'service_page_content_depth',
      title: 'Add Content Sections',
      taskType: 'content',
      wpField: 'content',
    },
    uniqueness: {
      opportunityType: 'service_page_uniqueness',
      title: 'Add Unique Content',
      taskType: 'content',
      wpField: 'content',
    },
  };

  const handleFixCheck = useCallback(
    async (checkKey: string) => {
      if (!page || pageKeywords.length === 0) return;
      setFixingCheck(checkKey);
      setCopilotResult(null);

      const primaryKw = pageKeywords[0];
      const config = CHECK_CONFIG[checkKey];
      if (!config) {
        setFixingCheck(null);
        return;
      }

      const checkNames: Record<string, string> = {
        faqSchema: 'FAQ Schema',
        serviceSchema: 'Service Schema',
        pricing: 'Pricing Data',
        titleFreshness: 'Title Freshness',
        contentDepth: 'Content Depth',
        uniqueness: 'Template Uniqueness',
      };

      try {
        // Step 1: Call getOpportunitySuggestions directly for this specific check
        const snap = primaryKw.latestSnapshot;
        const result = await seoAiService.getOpportunitySuggestions({
          keyword: primaryKw.keyword,
          opportunityType: config.opportunityType,
          opportunityTitle: config.title,
          opportunityDescription:
            healthResult?.checks?.[checkKey as keyof typeof healthResult.checks]?.detail ?? '',
          position: snap?.position ?? null,
          ctr: snap?.ctr ?? null,
          clicks: snap?.clicks ?? null,
          impressions: snap?.impressions ?? null,
          serpSummary: null,
          pageUrl: page.url,
        });

        const suggestions = result?.suggestions ?? [];
        if (suggestions.length === 0) {
          setCopilotResult(
            `No suggestions generated for ${checkNames[checkKey]}. The AI may need more context — try running the full copilot instead.`
          );
          setFixingCheck(null);
          return;
        }

        // Log for debugging
        console.log(
          `[PageDetail] Fix ${checkKey}: got ${suggestions.length} suggestions`,
          suggestions.map((s: any) => ({ type: s.type, label: s.label, valueLen: s.value?.length }))
        );

        // Step 2: Create tasks and auto-apply to WordPress
        let applied = 0;
        let tasksCreated = 0;
        const wpPost = wpConnected
          ? await wordpressService.findPostByUrl(projectId, page.url)
          : null;
        console.log(
          `[PageDetail] wpConnected=${wpConnected}, wpPost=`,
          wpPost,
          `pageUrl=${page.url}`
        );

        for (const sug of suggestions.slice(0, 3)) {
          // Determine the task type from the suggestion type
          const sugTypeMap: Record<string, string> = {
            title_tag: 'title_tag',
            meta_description: 'meta_description',
            h1: 'content',
            h2_section: 'content',
            paragraph: 'content',
            faq_entry: 'content',
            schema_markup: 'schema',
            schema: 'schema',
            json_ld: 'schema',
            internal_link: 'internal_link',
          };
          const taskType = sugTypeMap[sug.type] ?? config.taskType;
          const wpFieldMap: Record<string, string> = {
            title_tag: 'seoTitle',
            meta_description: 'seoDescription',
            schema_markup: 'schema',
            schema: 'schema',
            json_ld: 'schema',
            content: 'content',
            h1: 'content',
            h2_section: 'content',
            paragraph: 'content',
            faq_entry: 'content',
          };
          const wpField = wpFieldMap[sug.type] ?? config.wpField;

          if (!sug.value) continue;

          // For schema: strip <script> wrapper if GPT included it — the WP edge function adds it
          let cleanValue = sug.value;
          if (wpField === 'schema') {
            cleanValue = cleanValue
              .replace(/<script[^>]*type=["']application\/ld\+json["'][^>]*>/gi, '')
              .replace(/<\/script>/gi, '')
              .trim();
          }

          // Create the task
          await seoTasksService.create({
            projectId,
            keywordId: primaryKw.id,
            title: `[Copilot] ${sug.label ?? config.title}`,
            description: `${sug.reasoning ?? config.title}\n\nAuto-generated for keyword "${
              primaryKw.keyword
            }".`,
            taskType: taskType as SeoTask['taskType'],
            suggestedValue: cleanValue,
            priority: 'high',
          });
          tasksCreated++;

          // Auto-apply to WordPress
          if (wpPost && cleanValue) {
            try {
              console.log(
                `[PageDetail] Applying to WP: field=${wpField}, type=${sug.type}, taskType=${taskType}, valueLen=${cleanValue.length}`
              );
              const applyResult = await wordpressService.applyChanges({
                projectId,
                postId: wpPost.postId,
                postType: wpPost.postType,
                keywordId: primaryKw.id,
                pageUrl: page.url,
                changes: { [wpField]: cleanValue },
              });
              console.log(`[PageDetail] WP apply result:`, applyResult);
              applied++;
            } catch (applyErr) {
              console.error(`[PageDetail] WP apply failed:`, applyErr);
              // Individual apply failure — task was still created
            }
          }
        }

        // Step 3: Refetch data
        if (applied > 0) {
          refetchChanges();
          // Wait a moment for WordPress cache, then re-check health
          setTimeout(() => handleCheckHealth(), 3000);
        }

        setCopilotResult(
          applied > 0
            ? `${checkNames[checkKey]}: ${applied} change${
                applied > 1 ? 's' : ''
              } applied to WordPress.`
            : tasksCreated > 0
            ? `${checkNames[checkKey]}: ${tasksCreated} task${
                tasksCreated > 1 ? 's' : ''
              } created.${
                !wpConnected
                  ? ' Connect WordPress to auto-apply.'
                  : wpPost
                  ? ''
                  : ' WordPress post not found for this URL.'
              }`
            : `No actionable suggestions for ${checkNames[checkKey]}.`
        );
      } catch (err) {
        setCopilotResult(
          `Failed to fix ${checkNames[checkKey]}: ${(err as Error).message ?? 'unknown error'}`
        );
      } finally {
        setFixingCheck(null);
      }
    },
    [page, pageKeywords, projectId, wpConnected, healthResult, refetchChanges, handleCheckHealth]
  );

  // Breadcrumb
  useEffect(() => {
    if (page?.path) setBreadcrumbDetail(page.path);
    return () => setBreadcrumbDetail(null);
  }, [page?.path, setBreadcrumbDetail]);

  return (
    <SeoPageDetailComponent
      page={page ?? null}
      isLoading={isLoadingPage}
      projectId={projectId}
      keywords={pageKeywords}
      difficultyMap={difficultyMap}
      ahrefsDomainSnapshot={ahrefsDomainSnapshot ?? null}
      ahrefsNewBacklinks={newBacklinks.length}
      ahrefsLostBacklinks={lostBacklinks.length}
      appliedChanges={pageChanges}
      copilotByKeyword={copilotByKeyword}
      isServicePage={isServicePage}
      healthResult={healthResult}
      isCheckingHealth={isCheckingHealth}
      onCheckHealth={handleCheckHealth}
      isRunningCopilot={isRunningCopilot}
      copilotResult={copilotResult}
      onRunCopilot={handleRunCopilot}
      fixingCheck={fixingCheck}
      onFixCheck={handleFixCheck}
      generatedFaqs={generatedFaqs}
      generatedFaqSchema={generatedFaqSchema}
      isGeneratingFaqs={isGeneratingFaqs}
      isPublishingFaqs={isPublishingFaqs}
      onGenerateFaqs={handleGenerateFaqs}
      onPublishFaqs={handlePublishFaqs}
      onClearFaqPreview={handleClearFaqPreview}
    />
  );
};

export default SeoPageDetailPage;
